import { useCallback, useState } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { MutationState } from 'src/app/shared/utils/async';

import type { UpdateTaskErrorCode } from './constants';
import { executeMutation } from './mutate';
import type { UpdatePersonalizedPetitionTaskInput, UpdatePersonalizedPetitionTaskResponse } from './types';

type State = MutationState<{ data: UpdatePersonalizedPetitionTaskResponse }, { error: UpdateTaskErrorCode }>;

type Options = {
	onSuccess?: (response: UpdatePersonalizedPetitionTaskResponse) => void;
	onError?: (error: UpdateTaskErrorCode) => void;
};

export function useUpdatePersonalizedPetitionTask({ onSuccess, onError }: Options = {}) {
	const utilityContext = useUtilityContext();
	const [state, setState] = useState<State>({ status: 'idle' });

	const mutate = useCallback(
		async (input: UpdatePersonalizedPetitionTaskInput) => {
			setState({ status: 'loading' });
			const result = await executeMutation(input, utilityContext);
			setState(result);
			if (result.status === 'loaded') onSuccess?.(result.data);
			else onError?.(result.error);
			return result;
		},
		[onError, onSuccess, utilityContext],
	);

	const reset = useCallback(() => setState({ status: 'idle' }), []);

	return { mutate, reset, ...state };
}
